/**
 * Single constants home for bank-transaction category display (label +
 * badge color), mirroring the pattern established in invoiceTaxonomy.js
 * for invoice taxonomy. Before this file existed, TransactionsPage.jsx,
 * MonthReportPage.jsx, and EntitiesDetailedTab.jsx each hand-rolled their
 * own copy of this mapping; two of the three had already drifted out of
 * sync with the backend TransactionCategory enum (missing booking_com,
 * direct_booking, bank_fee). Every consumer must import from here.
 *
 * Mirrors backend enums.TransactionCategory (backend/enums.py) -- a
 * parity test (backend/tests/test_transaction_category_parity.py) pins
 * every enum value to an entry here so a future 13th value can't go
 * missing the way three of twelve already had.
 */

export const TRANSACTION_CATEGORIES = [
  { key: 'revenue', label: 'Revenue', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  { key: 'revenue_mgmt_statement', label: 'Rev - Mgmt Statement', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  { key: 'supplier_payment', label: 'Supplier', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  { key: 'intercompany', label: 'Intercompany', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  { key: 'tax_payment', label: 'Tax', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  { key: 'subscription', label: 'Subscription', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  { key: 'personal', label: 'Personal', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400' },
  { key: 'loan', label: 'Loan', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  { key: 'airbnb', label: 'Airbnb', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400' },
  { key: 'booking_com', label: 'Booking.com', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { key: 'direct_booking', label: 'Direct Booking', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' },
  { key: 'bank_fee', label: 'Bank Fee', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  { key: 'cashback', label: 'Cashback', color: 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-400' },
  { key: 'internal_transfer', label: 'Internal Transfer', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  { key: 'interest_income', label: 'Interest', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  { key: 'property_portfolio', label: 'Property Portfolio', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { key: 'other', label: 'Other', color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400' },
];

// Same shape as the old per-file `categoryConfig` object ({label, color}
// keyed by category), so existing `categoryConfig[tx.category]?.label`
// call sites keep working unchanged after switching to an aliased import.
export const TRANSACTION_CATEGORY_MAP = Object.fromEntries(
  TRANSACTION_CATEGORIES.map((c) => [c.key, { label: c.label, color: c.color }]),
);

export const TRANSACTION_CATEGORY_LABELS = Object.fromEntries(
  TRANSACTION_CATEGORIES.map((c) => [c.key, c.label]),
);

export function transactionCategoryLabel(key) {
  return TRANSACTION_CATEGORY_LABELS[key] || TRANSACTION_CATEGORY_LABELS.other;
}

// Revenue-family keys: mirrors backend enums.REVENUE_CATEGORIES. Any UI
// that special-cases 'revenue' for P&L/reporting or double-count warnings
// must check membership here instead, so revenue_mgmt_statement is never
// silently excluded.
export const REVENUE_FAMILY_CATEGORY_KEYS = ['revenue', 'revenue_mgmt_statement'];
